// The onboarding path (TX-US-0042): a counterparty as a case moving through Prospect, KYC, Credit
// and Legal to its outcome, drawn as a row of pointed stages read left to right. Each of the three
// middle stages opens its sheet beneath the path; the open stage is in the address, so a link and
// Back behave as the tabs do. A sheet that is closed is hidden, not unmounted, so an unsaved draft
// survives choosing another stage. The actions sit at the path's right end.
//
// On a phone the path scrolls sideways with the current stage in view, and the actions wrap
// beneath it.

import { useSearchParams } from 'react-router';

import { useMe } from '../../MeContext.js';
import PathStages from '../../ui/PathStages.jsx';
import CreditAssessment from '../credit/CreditAssessment.jsx';
import KycReview from '../kyc/KycReview.jsx';
import LegalReview from '../legal/LegalReview.jsx';
import PathActions from './PathActions.jsx';
import { stageFrom, stageSearch, stagesOf } from './path.js';

const STATE_WORDS = {
  done: 'passed',
  current: 'current',
  warning: 'current, override requested',
  skipped: 'skipped',
  next: 'not reached',
  rejected: 'rejected',
};

export default function OnboardingPath({ record, sheets, onKyc, onCredit, onChanged }) {
  const me = useMe();
  const [searchParams] = useSearchParams();
  const open = stageFrom(searchParams, record, me);
  // A stage with a sheet opens it.
  const stages = stagesOf(record, sheets.kyc?.sheet)
    .map((stage) => ({ ...stage, opens: stage.sheet ? `the ${stage.sheet}` : null }));
  const holds = (role) => Boolean(me?.roles?.includes(role));

  return (
    <section className="profile-panel onboarding" aria-label="Onboarding">
      <div className="onboarding-bar">
        <PathStages stages={stages} open={open} words={STATE_WORDS} scrollKey={record.status}
                    searchFor={(key) => stageSearch(searchParams, key)} />
        <PathActions record={record} sheets={sheets} onChanged={onChanged} />
      </div>

      <div className="path-sheet" hidden={open !== 'kyc'}>
        <KycReview counterparty={record} canEdit={holds('COMPLIANCE')} onSaved={onKyc} />
      </div>
      <div className="path-sheet" hidden={open !== 'credit'}>
        <CreditAssessment counterpartyId={record.counterparty_id} canEdit={holds('RISK')}
                          onSaved={onCredit} />
      </div>
      <div className="path-sheet" hidden={open !== 'legal'}>
        <LegalReview counterpartyId={record.counterparty_id} canEdit={holds('LEGAL')} />
      </div>
    </section>
  );
}
